import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, ShieldCheck, UserCheck, TrendingUp, 
  ChevronRight, CheckCircle2, FileText, FlaskConical, 
  Stethoscope, Users, Building2, ArrowRight, MessageSquare
} from 'lucide-react';
import { Button, Badge, Card, CardContent, CardHeader, CardTitle } from '@/ui';
import { useLocation } from 'wouter';

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function OccupationalMedicine() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBookDemo = () => {
    window.scrollTo(0, 0);
    setLocation("/book-demo");
  };

  const handleContact = () => {
    setLocation("/contact");
  };

  return (
    <div 
      className="min-h-screen flex flex-col font-sans bg-background relative"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      <main className="flex-1 relative z-10">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden pt-16 pb-20 lg:pt-24 lg:pb-32">
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div 
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="space-y-6"
              >
                <motion.div variants={fadeInUp}>
                  <Badge className="mb-6 border-none" style={{ backgroundColor: `${PRIMARY_BLUE}20`, color: PRIMARY_BLUE }}>
                    <span className="relative flex h-2 w-2 mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
                      <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: PRIMARY_BLUE }}></span>
                    </span>
                    New: Occupational Medicine Solutions
                  </Badge>
                </motion.div>
                
                <motion.h1 
                  variants={fadeInUp} 
                  className="text-4xl lg:text-5xl font-normal leading-[1.1] mb-6"
                  style={{ color: PRIMARY_BLUE }}
                >
                  Occupational Medicine That Works for{' '}
                  <span 
                    className="bg-clip-text text-transparent"
                    style={{
                      backgroundImage: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${ACCENT_COLOR} 100%)`
                    }}
                  >
                    Everyone
                  </span>
                </motion.h1>
                
                <motion.p variants={fadeInUp} className="text-xl text-gray-600 mb-8 leading-relaxed max-w-lg">
                  A unified platform connecting employers, clinicians, and employees—from pre-employment screens to return-to-work clearances.
                </motion.p>
                
                <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto font-bold gap-2 rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
                    style={{
                      backgroundColor: PRIMARY_BLUE,
                      color: "white",
                      boxShadow: "0 4px 0 #0a2d4d, 0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
                      border: "1px solid rgba(255,255,255,0.2)",
                    }}
                    onClick={handleBookDemo}
                  >
                    Request a Demo
                    <ArrowRight className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
                  </Button>
                </motion.div>
                
                <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-8 border-t border-border/60">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-2xl" style={{ color: PRIMARY_BLUE }}>
                      <Clock className="w-5 h-5" /> -60%
                    </div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">Clearance Time</div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-2xl" style={{ color: ACCENT_COLOR }}>
                      <ShieldCheck className="w-5 h-5" /> 99%
                    </div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">Compliance</div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-2xl" style={{ color: PRIMARY_BLUE }}>
                      <UserCheck className="w-5 h-5" /> 95%
                    </div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">Satisfaction</div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-purple-600 text-2xl">
                      <TrendingUp className="w-5 h-5" /> -45%
                    </div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">Resolution Time</div>
                  </div>
                </motion.div>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative hidden lg:block"
              >
                {/* Visual Concept: Overlapping Cards */}
                <div className="relative h-[500px] w-full">
                  {/* Background Card (Employer) */}
                  <div className="absolute top-0 right-0 w-3/4 h-[400px] bg-white rounded-xl shadow-xl border border-border p-6 transform rotate-3 z-10 transition-transform hover:rotate-1 hover:z-30">
                     <div className="flex justify-between items-center mb-6 border-b pb-4">
                        <div className="font-normal" style={{ color: PRIMARY_BLUE }}>Employer Portal</div>
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200">HR Admin View</Badge>
                     </div>
                     <div className="space-y-4">
                        <div className="flex gap-4">
                           <div className="flex-1 h-24 bg-slate-50 rounded-lg border border-dashed border-slate-200 flex flex-col justify-center items-center">
                              <div className="text-2xl text-slate-700">12</div>
                              <div className="text-xs text-slate-500">Pending Auth</div>
                           </div>
                           <div className="flex-1 h-24 bg-slate-50 rounded-lg border border-dashed border-slate-200 flex flex-col justify-center items-center">
                              <div className="text-2xl text-slate-700">45</div>
                              <div className="text-xs text-slate-500">Results Ready</div>
                           </div>
                        </div>
                        <div className="h-4 bg-slate-100 rounded w-full"></div>
                        <div className="h-4 bg-slate-100 rounded w-2/3"></div>
                        <div className="h-4 bg-slate-100 rounded w-3/4"></div>
                     </div>
                  </div>
                  
                  {/* Foreground Card (Clinical) */}
                  <div className="absolute bottom-0 left-0 w-3/4 h-[400px] bg-white rounded-xl shadow-2xl border border-border p-6 transform -rotate-3 z-20 transition-transform hover:rotate-0">
                     <div className="flex justify-between items-center mb-6 border-b pb-4">
                        <div className="font-normal" style={{ color: PRIMARY_BLUE }}>Clinical Ops</div>
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">Provider View</Badge>
                     </div>
                     <div className="space-y-4">
                       <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-100">
                          <div className="flex items-center gap-3">
                             <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs">JD</div>
                             <div>
                                <div className="text-sm">John Doe</div>
                                <div className="text-xs text-slate-500">DOT Physical</div>
                             </div>
                          </div>
                          <Badge variant="outline" className="text-xs border-orange-200 text-orange-600 bg-orange-50">In Progress</Badge>
                       </div>
                       
                       <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-100">
                          <div className="flex items-center gap-3">
                             <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xs">AS</div>
                             <div>
                                <div className="text-sm">Alice Smith</div>
                                <div className="text-xs text-slate-500">Drug Screen (5-Panel)</div>
                             </div>
                          </div>
                          <Badge variant="outline" className="text-xs border-green-200 text-green-600 bg-green-50">Completed</Badge>
                       </div>
                       
                       <div className="h-40 bg-slate-50 rounded-lg border border-dashed border-slate-200 flex items-center justify-center">
                          <div className="text-center">
                             <div className="text-sm text-slate-400">Workflow Timeline</div>
                             <div className="flex gap-1 mt-2 justify-center">
                                <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                                <div className="w-8 h-0.5 bg-blue-200 self-center"></div>
                                <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                                <div className="w-8 h-0.5 bg-slate-200 self-center"></div>
                                <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                             </div>
                          </div>
                       </div>
                     </div>
                  </div>
                  
                  {/* Connection Overlay */}
                  <div 
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30 text-white rounded-full p-4 shadow-lg"
                    style={{ backgroundColor: PRIMARY_BLUE }}
                  >
                    <ArrowRight className="w-6 h-6 animate-pulse" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SECTION 2: CLINICAL OPERATIONS */}
        <section className="py-20">
          <div className="container mx-auto px-4 bg-white/90 backdrop-blur-sm rounded-xl p-8 shadow-sm">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <Badge 
                variant="secondary" 
                className="mb-4"
                style={{ backgroundColor: `${ACCENT_COLOR}20`, color: ACCENT_COLOR }}
              >
                Clinical Operations
              </Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4" style={{ color: PRIMARY_BLUE }}>
                From Check-In to Clearance, Automated & Compliant
              </h2>
              <p className="text-lg text-gray-600">
                Automated chain-of-custody, DOT/Non-DOT exams, MRO review, and disposition—all in one compliant, auditable system.
              </p>
            </div>

            {/* Workflow Visualization */}
            <div className="mb-16 overflow-x-auto pb-8">
              <div className="flex min-w-[800px] justify-between relative px-4">
                {/* Connector Line */}
                <div className="absolute top-6 left-0 right-0 h-0.5 bg-slate-200 z-0"></div>
                
                {[
                  { step: "Auth", label: "Authorization", icon: FileText },
                  { step: "Intake", label: "Identity Verify", icon: UserCheck },
                  { step: "Collect", label: "eCCF & Chain", icon: FlaskConical },
                  { step: "Exam", label: "DOT/Non-DOT", icon: Stethoscope },
                  { step: "Review", label: "MRO Review", icon: ShieldCheck },
                  { step: "Packet", label: "Employer Packet", icon: Building2 },
                ].map((item, i) => (
                  <div key={i} className="relative z-10 flex flex-col items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full bg-white border-2 text-primary flex items-center justify-center shadow-sm"
                      style={{ borderColor: PRIMARY_BLUE }}
                    >
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div className="text-sm text-center max-w-[100px]">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Smart Work Queue", desc: "Kanban view with employer, protocol, stage, and timer tracking. Never miss an SLA.", icon: Clock },
                { title: "eCCF & Chain-of-Custody", desc: "Electronic CCF with barcode labels, observed collection toggle, and digital signatures.", icon: FlaskConical },
                { title: "DOT/Non-DOT Exams", desc: "Complete exam modules with vision, BP, UA, and disqualifying conditions checklist.", icon: FileText },
                { title: "Alcohol Testing Timer", desc: "15-minute observation timer with device capture and audible alerts for OSHA compliance.", icon: Clock },
                { title: "MRO Workspace", desc: "Non-negative case queue with donor contact log and verify/cancel/recollect workflow.", icon: ShieldCheck },
                { title: "AI Disposition Builder", desc: "Packet composer with MEC, drug/alcohol summary, and one-click delivery.", icon: CheckCircle2 },
              ].map((feature, i) => (
                <Card key={i} className="border-border/50 hover:shadow-md transition-shadow bg-white">
                  <CardHeader>
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                      style={{ backgroundColor: ACCENT_COLOR }}
                    >
                      <feature.icon className="w-5 h-5 text-white" />
                    </div>
                    <CardTitle className="text-xl font-normal" style={{ color: PRIMARY_BLUE }}>{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3: EMPLOYER PORTAL */}
        <section className="py-20">
          <div className="container mx-auto px-4 bg-white/90 backdrop-blur-sm rounded-xl p-8 shadow-sm">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 relative">
                 {/* Mockup Container */}
                 <div className="rounded-xl border-8 border-slate-900 shadow-2xl overflow-hidden bg-slate-50">
                    {/* Mockup Header */}
                    <div className="bg-slate-900 text-white p-3 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      </div>
                      <div className="text-xs text-slate-400 ml-4 bg-slate-800 px-3 py-1 rounded-full flex-1 text-center">portal.aiscendus.com</div>
                    </div>
                    {/* Mockup Body */}
                    <div className="p-6 h-[400px] overflow-y-auto">
                      <div className="flex justify-between items-center mb-6">
                         <h3 className="text-xl font-normal" style={{ color: PRIMARY_BLUE }}>Dashboard</h3>
                         <Button size="sm">New Authorization</Button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg shadow-sm border">
                          <div className="text-sm text-slate-500">Pending</div>
                          <div className="text-2xl" style={{ color: PRIMARY_BLUE }}>12</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-sm border">
                          <div className="text-sm text-slate-500">Completed</div>
                          <div className="text-2xl" style={{ color: ACCENT_COLOR }}>85</div>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                        <div className="px-4 py-3 bg-slate-50 border-b text-sm">Recent Results</div>
                        {[1, 2, 3].map((r) => (
                          <div key={r} className="p-4 border-b last:border-0 flex justify-between items-center">
                            <div>
                               <div className="font-medium">Employee #{1000 + r}</div>
                               <div className="text-xs text-slate-500">Pre-Employment Screen</div>
                            </div>
                            <Badge variant={r === 1 ? "secondary" : "default"} className={r === 1 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}>
                              {r === 1 ? "Pending" : "Cleared"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                 </div>
                 
                 {/* Floating Feature Badge */}
                 <motion.div 
                   animate={{ y: [0, -10, 0] }}
                   transition={{ repeat: Infinity, duration: 4 }}
                   className="absolute -right-8 top-20 bg-white p-4 rounded-lg shadow-lg border border-border hidden md:block"
                 >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-green-600"
                        style={{ backgroundColor: `${ACCENT_COLOR}20` }}
                      >
                        <CheckCircle2 className="w-6 h-6" style={{ color: ACCENT_COLOR }} />
                      </div>
                      <div>
                        <div className="font-normal" style={{ color: PRIMARY_BLUE }}>Instant Auth</div>
                        <div className="text-xs text-muted-foreground">QR Code Generated</div>
                      </div>
                    </div>
                 </motion.div>
              </div>
              
              <div className="order-1 lg:order-2 space-y-8">
                <div>
                  <Badge 
                    variant="outline" 
                    className="px-3 py-1 rounded-full text-sm mb-4"
                    style={{ borderColor: `${ACCENT_COLOR}20`, color: ACCENT_COLOR, backgroundColor: `${ACCENT_COLOR}05` }}
                  >
                    Employer Self-Service
                  </Badge>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4" style={{ color: PRIMARY_BLUE }}>Your Workforce Health Dashboard</h2>
                  <p className="text-lg text-gray-600">
                    Create authorizations, track results, download packets, and pay invoices—all from a self-service portal designed for HR and Safety teams.
                  </p>
                </div>
                
                <div className="space-y-6">
                  {[
                    { title: "Protocol Designer", desc: "Configure drug testing panels, alcohol screening, and fit testing protocols specific to your roles." },
                    { title: "Instant Authorization", desc: "Generate QR codes and consent packets instantly for employees." },
                    { title: "Real-Time Results", desc: "Download cleared packets immediately after MRO review. No phone tag." },
                    { title: "Compliance Tracking", desc: "Monitor DOT expirations and random pool compliance automatically." }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4">
                       <div 
                         className="mt-1 w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                         style={{ backgroundColor: `${ACCENT_COLOR}20`, color: ACCENT_COLOR }}
                       >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                       </div>
                       <div>
                          <h4 className="font-normal" style={{ color: PRIMARY_BLUE }}>{item.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{item.desc}</p>
                       </div>
                    </div>
                  ))}
                </div>
                
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="group"
                  style={{ borderColor: `${PRIMARY_BLUE}40`, color: PRIMARY_BLUE }}
                >
                  Explore Employer Features 
                  <ChevronRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: CONNECTED ECOSYSTEM */}
        <section className="py-24 relative overflow-hidden">
           <div 
             className="container mx-auto px-4 relative z-10 text-center rounded-xl p-8 shadow-xl text-white"
             style={{ 
               background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, #0d3b6e 100%)`,
             }}
           >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-6 text-white">Clinic + Employer, Always In Sync</h2>
              <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-16">
                 Real-time visibility across the entire workforce health lifecycle. When we act, you know.
              </p>
              
              <div className="grid md:grid-cols-4 gap-8">
                 {[
                   { icon: Building2, label: "Authorization", sub: "Status: In Queue" },
                   { icon: FlaskConical, label: "Collection", sub: "Status: Processing" },
                   { icon: ShieldCheck, label: "MRO Review", sub: "Status: Verified" },
                   { icon: FileText, label: "Clearance", sub: "Packet Delivered" }
                 ].map((item, i) => (
                   <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 rounded-xl hover:bg-white/20 transition-colors">
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-normal text-lg text-white">{item.label}</h3>
                      <div className="mt-1" style={{ color: ACCENT_COLOR }}>{item.sub}</div>
                   </div>
                 ))}
              </div>
              
              <div className="mt-16 flex flex-wrap justify-center gap-8 text-blue-200 text-sm">
                 <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" style={{ color: ACCENT_COLOR }} /> SFTP Integration
                 </div>
                 <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" style={{ color: ACCENT_COLOR }} /> HL7/ORU Feeds
                 </div>
                 <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" style={{ color: ACCENT_COLOR }} /> HRIS Webhooks
                 </div>
                 <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" style={{ color: ACCENT_COLOR }} /> Role-Based Access
                 </div>
              </div>
           </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-20">
           <div className="container mx-auto px-4 text-center bg-white/90 backdrop-blur-sm rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-6" style={{ color: PRIMARY_BLUE }}>
                Ready to Transform Your Workforce Health Program?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Join leading employers who have reduced clearance times by 60% with Aiscendus.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                 <Button 
                   size="lg" 
                   className="w-full sm:w-auto font-bold gap-2 rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
                   style={{
                     backgroundColor: PRIMARY_BLUE,
                     color: "white",
                     boxShadow: "0 4px 0 #0a2d4d, 0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
                     border: "1px solid rgba(255,255,255,0.2)",
                   }}
                   onClick={handleBookDemo}
                 >
                   Request a Demo
                   <ArrowRight className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
                 </Button>
                 <Button 
                   size="lg" 
                   className="w-full sm:w-auto gap-2 font-bold rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
                   style={{
                     backgroundColor: "white",
                     color: PRIMARY_BLUE,
                     boxShadow: "0 4px 0 rgba(0,0,0,0.1), 0 6px 20px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2)",
                     border: `1px solid ${PRIMARY_BLUE}40`,
                   }}
                   onClick={handleContact}
                 >
                   <MessageSquare className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
                   Contact Sales
                 </Button>
              </div>
           </div>
        </section>
      </main>
    </div>
  );
}
